import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Hooks, Context } from "@xt/xtbase";
import ShareModal from "@/components/App/shareModal";
import { get_profileInfo } from "api/referral";
import { getOrigin } from "utils/method";
import store from "store";
import {
  buildPositionShareSlides,
  resolvePositionProfitCurrency,
} from "@/utils/sharePosterSlides";

const { useTranslation } = Hooks;
const { XtContext } = Context;

function getFineLang(lang: string | undefined): string {
  if (!lang) return "en";
  return lang === "ko" ? "en" : lang;
}

function getInviteUrl(inviteCode: string, lang: string | undefined): string {
  if (!inviteCode) return getOrigin("www") || "";
  const base = getOrigin("www") || "";
  const pathLang = getFineLang(lang);
  const sep = base.endsWith("/") ? "" : "/";
  return `${base}${sep}${pathLang}/accounts/register?ref=${encodeURIComponent(inviteCode)}`;
}

/** 分享海报所需数据：当前持仓 + 实时价格/盈亏 */
export interface SharePosterPayload {
  position: {
    pair: string;
    side: string;
    leverage: string;
    entryPrice: string;
    quantity: string;
    margin: string;
  };
  realTimePrice: string;
  realTimeProfitLossValue: string;
  isNegative: boolean;
}

interface SharePosterModalProps {
  open: boolean;
  onClose: () => void;
  payload: SharePosterPayload | null;
}

export default function SharePosterModal({ open, onClose, payload }: SharePosterModalProps) {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const lang = appState?.lang;
  const [profileInviteCode, setProfileInviteCode] = useState<string | null>(null);

  const userInfo = store.user.userInfo as Record<string, unknown> | null;
  const userName = (userInfo?.nickname as string) || (userInfo?.userId as number)?.toString() || "User name";
  const fallbackInviteCode =
    (userInfo?.inviteCode as string) || (userInfo?.userId as number)?.toString()?.slice(-8) || "";
  const inviteCode = profileInviteCode ?? fallbackInviteCode;

  useEffect(() => {
    if (!open) return;
    get_profileInfo()
      .then((data: { inviteCode?: string }) => {
        const code = data?.inviteCode;
        if (code) setProfileInviteCode(String(code));
      })
      .catch(() => setProfileInviteCode(null));
  }, [open]);

  const shareQrUrl = useMemo(() => getInviteUrl(inviteCode, lang), [inviteCode, lang]);

  const slides = useMemo(() => {
    if (!payload) return [];
    const sideText =
      payload.position.side === "buy" ? t("cfd.buyUp") || "Long" : t("cfd.buyDown") || "Short";
    return buildPositionShareSlides(payload, {
      userName,
      shareTime: moment().format("YYYY-MM-DD HH:mm"),
      productName: t("cfd.productName") || "Futures",
      sideText,
      profitCurrency: resolvePositionProfitCurrency(payload.position.pair),
    });
  }, [payload, t, userName]);

  if (!payload) return null;

  return (
    <ShareModal
      isShowModal={open}
      onClose={onClose}
      qrUrl={shareQrUrl}
      inviteCode={inviteCode}
      slides={slides}
      hideSubtitle
    />
  );
}
